import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { RootState } from 'slices'
import { useLabelWidth } from 'hooks/use-label-width'
import TextBox, { TextBoxRef } from 'devextreme-react/text-box'
import useResizeObserver from '@react-hook/resize-observer'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import { FormErrors } from 'components/forms/form-errors'
import './index.scss'


export const TextInput = ({label, readOnly=false, disabled=false, showClearButton=false, ...props}: any) => {
    const [field, meta] = useField(props)
    const { setFieldValue } = useFormikContext()
    const inputRef = useRef<TextBoxRef>(null)
    const [fieldWidth, setFieldWidth] = useState<any>(null)
    const [inFocus, setInFocus] = useState(false)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const getLabelWidth = useLabelWidth(formFieldType)

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((inputRef.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        if (isFloatingLabel) {
            const textBox: any = inputRef.current
            const floatingLabel: HTMLElement = textBox?.instance()?.element().querySelector('.dx-label')

            if (floatingLabel) {
                const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                const floatingLabelWidth = getLabelWidth(label, fontFamily)
                const input: any = textBox?.instance()?.element().querySelector('input.dx-texteditor-input')

                fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+18)}px`)
            }

            if (field.value) {
                textBox?.instance()?.element().classList.add('input-has-value')
                floatingLabel && (floatingLabel.style.left = `calc(100% - ${floatingLabel.offsetWidth}px)`)
            }

            else {
                textBox?.instance()?.element().classList.remove('input-has-value')
                floatingLabel && (floatingLabel.style.left = inFocus ? `calc(100% - ${floatingLabel.offsetWidth}px)` : '0px')
            }
        }

    },[field.value, getLabelWidth, label, fieldWidth, inFocus, isFloatingLabel])

    const handleChange = (e: any) => {
        const value = e.value  // && e.value !== '' ? e.value : null
        setFieldValue(field.name, value, true)
    }

    const handleBlur = (e: any) => {
        field.onBlur(e.event)
        setInFocus(false)
    }

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const ddButtons = useMemo<any>(() => ([
        { name: 'clear' },
        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }
        }
    ]), [label])

    return (
        <div className='rb-item mb-1'>
            <div>
                <label htmlFor={props.id || props.name} className='dx-field-label'>{label}</label>
                <div className='dx-field-value'>
                    <TextBox
                        ref={inputRef}
                        label={label}
                        labelMode= 'floating'
                        buttons={ddButtons}
                        name={field.name}
                        inputAttr={{id: props.name}}
                        value={field.value}
                        onValueChanged={handleChange}
                        onFocusOut={handleBlur}
                        onFocusIn={onFocusIn}
                        className='dx-field-input'
                        disabled={disabled}
                        readOnly={readOnly}
                        isValid={!(meta.touched && meta.error)}
                        mode={props.mode || 'text'}
                        mask={props.mask}
                        maskRules={props.maskRules}
                        showClearButton={showClearButton && field.value !== null}
                        height={26}
                    />
                </div>
            </div>
            <FormErrors meta={meta} />
        </div>
    )
}
